import { isRunStateInProgress, normalizeRunStateValue } from '@/lib/pieveRunState.js';

const FAILED_RUN_STATES = new Set(['error', 'failed']);
const QUEUED_RUN_STATES = new Set(['queued', 'pending', 'starting']);
const CANCELED_RUN_STATES = new Set(['canceled', 'cancelled', 'canceling', 'cancelling']);

/**
 * Where a thread's latest run stands, as one execution state with a short line on what is
 * happening and what comes next. Returns null when the thread has no run to describe.
 * - waiting: an approval or question is open (`pendingCount`), whatever the run state says.
 * - queued / running: the run is in progress; `currentStep` names what Pieve is doing.
 * - failed: the run ended in error (`blockerMessage` explains why when the API sent it).
 * - done: the run finished; the person can follow up.
 */
const resolveThreadExecution = ({ runState, pendingCount = 0, currentStep = '', blockerMessage = '', hasActivity = false }) => {
  const value = normalizeRunStateValue(runState);

  if (pendingCount > 0) {
    return { state: 'waiting', detail: pendingCount > 1 ? `${pendingCount} decisions open` : 'Approve or deny below', next: 'Pieve continues as soon as you decide.' };
  }

  if (QUEUED_RUN_STATES.has(value)) {
    return { state: 'queued', detail: 'Starting', next: 'Pieve picks this up in a moment.' };
  }

  if (isRunStateInProgress(value) && !CANCELED_RUN_STATES.has(value)) {
    return { state: 'running', detail: currentStep || 'Working on it', next: 'You can add details while Pieve works.' };
  }

  if (FAILED_RUN_STATES.has(value)) {
    return { state: 'failed', detail: blockerMessage || 'The run stopped with an error', next: 'Send a follow-up to try again or change the request.' };
  }

  if (CANCELED_RUN_STATES.has(value)) {
    return { state: 'failed', detail: 'Stopped', next: 'Send a follow-up to pick it up again.' };
  }

  if (!hasActivity) {
    return null;
  }

  return { state: 'done', detail: '', next: 'Ask a follow-up, or start a new request.' };
};

export default resolveThreadExecution;
